import { Check, Copy, Download, Loader2, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { logger } from "@/lib/logger";

interface InstructionsDialogProps {
  projectId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Copy button that appears in each code block. */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="text-muted-foreground hover:text-foreground absolute top-2 right-2 rounded p-1 transition-colors"
      title="Copy to clipboard"
    >
      {copied ? <Check className="size-3.5 text-green-500" /> : <Copy className="size-3.5" />}
    </button>
  );
}

/** Custom markdown components for links and code blocks. */
const markdownComponents: Components = {
  a: ({ href, children, ...props }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-accent underline underline-offset-2 hover:opacity-80"
      {...props}
    >
      {children}
    </a>
  ),
  pre: ({ children, ...props }) => {
    // Extract text content from the code element inside <pre>
    const codeText = extractText(children);
    return (
      <div className="relative">
        <CopyButton text={codeText} />
        <pre className="bg-primary/5 overflow-x-auto rounded-md text-sm text-current" {...props}>
          {children}
        </pre>
      </div>
    );
  },
};

/** Recursively extract text content from React children. */
function extractText(node: React.ReactNode): string {
  if (typeof node === "string") {
    return node;
  }
  if (typeof node === "number") {
    return String(node);
  }
  if (!node) {
    return "";
  }
  if (Array.isArray(node)) {
    return node.map(extractText).join("");
  }
  if (typeof node === "object" && "props" in node) {
    return extractText((node as { props: { children?: React.ReactNode } }).props.children);
  }
  return "";
}

export function InstructionsDialog({ projectId, open, onOpenChange }: InstructionsDialogProps) {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Loading is derived: while the dialog is open without a result yet.
  const loading = open && content === null && error === null;

  // Fetch instructions when dialog opens or projectId changes, with abort to prevent races.
  useEffect(() => {
    if (!open) {
      return;
    }

    const controller = new AbortController();

    fetch(`/api/projects/${projectId}/instructions`, { signal: controller.signal })
      .then(async (resp) => {
        if (!resp.ok) {
          const body = await resp.json().catch(() => null);
          throw new Error(body?.detail ?? "Failed to load instructions");
        }
        return resp.text();
      })
      .then((text) => {
        if (!controller.signal.aborted) {
          setContent(text);
        }
      })
      .catch((err) => {
        if (controller.signal.aborted || (err as Error).name === "AbortError") {
          return;
        }
        logger.error("[instructions] fetch failed:", err);
        setError(err instanceof Error ? err.message : "Failed to load instructions");
      });

    return () => {
      controller.abort();
      // Reset for next open so the loading state is restored. setState in
      // cleanup is allowed by the react-hooks rule.
      setContent(null);
      setError(null);
    };
  }, [open, projectId]);

  const fetchInstructions = useCallback(async () => {
    // Loading flips back on automatically once both content and error are
    // null (see the derived `loading` above).
    setContent(null);
    setError(null);
    try {
      const resp = await fetch(`/api/projects/${projectId}/instructions`);
      if (!resp.ok) {
        const body = await resp.json().catch(() => null);
        throw new Error(body?.detail ?? "Failed to load instructions");
      }
      const text = await resp.text();
      setContent(text);
    } catch (err) {
      logger.error("[instructions] fetch failed:", err);
      setError(err instanceof Error ? err.message : "Failed to load instructions");
    }
  }, [projectId]);

  const handleDownload = () => {
    if (!content) {
      return;
    }
    const blob = new Blob([content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "INSTRUCTIONS.md";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div className="flex items-start gap-3">
            {content && (
              <button
                type="button"
                onClick={handleDownload}
                className="bg-primary text-primary-foreground mt-0.5 inline-flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium shadow-sm transition-opacity hover:opacity-90"
                title="Download INSTRUCTIONS.md"
              >
                <Download className="size-3.5" />
              </button>
            )}
            <div>
              <DialogTitle>How to get started</DialogTitle>
              <DialogDescription>
                Setup instructions for running the project locally
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="max-h-[70vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="text-muted-foreground size-6 animate-spin" />
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <X className="text-destructive size-6" />
              <p className="text-muted-foreground text-sm">{error}</p>
              <Button variant="outline" size="sm" onClick={fetchInstructions}>
                Try again
              </Button>
            </div>
          )}

          {content && (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {content}
              </ReactMarkdown>
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
